import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Eye,
  BookOpen,
  Clock,
  Users,
  Heart,
  MoreVertical,
  ChevronLeft,
  ChevronRight
} from 'lucide-react'
import { RootState } from '../../store'
import {
  fetchAllSummaryCardsForAdmin,
  deleteSummaryCard,
  toggleSummaryCardPublish
} from '../../store/slices/summarySlice'
import { unwrapResult } from '@reduxjs/toolkit'
import { SummaryCard } from '../../types'
import { LGS_SUBJECTS, DIFFICULTY_LEVELS } from '../../constants/lgsSubjects'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'
import Badge from '../../components/ui/Badge'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import Modal from '../../components/ui/Modal'
import { formatDistanceToNow } from 'date-fns'
import { tr } from 'date-fns/locale'

interface FilterState {
  subject: string
  difficulty: string
  isPublished: string
  sortBy: 'createdAt' | 'title' | 'viewCount' | 'likeCount'
  sortOrder: 'asc' | 'desc'
}

const AdminSummaryCards: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { cards, isLoading, error, pagination } = useSelector((state: RootState) => state.summary)
  
  const [searchTerm, setSearchTerm] = useState('')
  const [filters, setFilters] = useState<FilterState>({
    subject: '',
    difficulty: '',
    isPublished: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  })
  const [showFilters, setShowFilters] = useState(false)
  const [selectedCard, setSelectedCard] = useState<SummaryCard | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(10)

  // Load summary cards
  useEffect(() => {
    loadSummaryCards()
  }, [currentPage, filters, searchTerm])

  const loadSummaryCards = async () => {
    try {
      await dispatch(fetchAllSummaryCardsForAdmin())
    } catch (error) {
      console.error('Error loading summary cards:', error)
    }
  }

  const handleSearch = (value: string) => {
    setSearchTerm(value)
    setCurrentPage(1)
  }

  const handleFilterChange = (key: keyof FilterState, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setCurrentPage(1)
  }

  const handleDelete = async () => {
    if (!selectedCard) return
    
    try {
      await dispatch(deleteSummaryCard(selectedCard.id))
      setShowDeleteModal(false)
      setSelectedCard(null)
      loadSummaryCards()
    } catch (error) {
      console.error('Error deleting summary card:', error)
      alert('Silme işlemi sırasında bir hata oluştu')
    }
  }

  const handleTogglePublish = async (card: SummaryCard) => {
    try {
      await dispatch(toggleSummaryCardPublish(card.id))
      loadSummaryCards()
    } catch (error) {
      console.error('Error toggling publish status:', error)
      alert('Yayın durumu değiştirme sırasında bir hata oluştu')
    }
  }

  const getSubjectName = (subjectId: string) => {
    return LGS_SUBJECTS.find(s => s.id === subjectId)?.name || subjectId
  }

  const getDifficultyLabel = (difficulty: string) => {
    return DIFFICULTY_LEVELS.find(d => d.value === difficulty)?.label || difficulty
  }

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'success'
      case 'medium': return 'warning'
      case 'hard': return 'error'
      default: return 'secondary'
    }
  }

  if (isLoading && cards.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Özet Kartları Yönetimi
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Toplam {pagination?.total || 0} özet kartı
              </p>
            </div>
            <Button
              onClick={() => navigate('/admin/summary-cards/create')}
              className="flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>Yeni Özet Kartı</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filters */}
        <Card className="p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Özet kartlarında ara..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant="outline"
                size="sm"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filtreler
              </Button>
            </div>
          </div>

          {showFilters && (
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Ders
                  </label>
                  <select
                    value={filters.subject}
                    onChange={(e) => handleFilterChange('subject', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Tüm Dersler</option>
                    {LGS_SUBJECTS.map((subject) => (
                      <option key={subject.id} value={subject.id}>
                        {subject.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Zorluk
                  </label>
                  <select
                    value={filters.difficulty}
                    onChange={(e) => handleFilterChange('difficulty', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Tüm Seviyeler</option>
                    {DIFFICULTY_LEVELS.map((level) => (
                      <option key={level.value} value={level.value}>
                        {level.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Durum
                  </label>
                  <select
                    value={filters.isPublished}
                    onChange={(e) => handleFilterChange('isPublished', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Tüm Durumlar</option>
                    <option value="true">Yayında</option>
                    <option value="false">Taslak</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Sıralama
                  </label>
                  <select
                    value={filters.sortBy}
                    onChange={(e) => handleFilterChange('sortBy', e.target.value as FilterState['sortBy'])}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="createdAt">Oluşturma Tarihi</option>
                    <option value="title">Başlık</option>
                    <option value="viewCount">Görüntülenme</option>
                    <option value="likeCount">Beğeni</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Yön
                  </label>
                  <select
                    value={filters.sortOrder}
                    onChange={(e) => handleFilterChange('sortOrder', e.target.value as FilterState['sortOrder'])}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="desc">Azalan</option>
                    <option value="asc">Artan</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Summary Cards Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : cards.length === 0 ? (
          <Card className="p-12 text-center">
            <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Henüz özet kartı bulunmuyor
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              İlk özet kartınızı oluşturmak için başlayın.
            </p>
            <Button onClick={() => navigate('/admin/summary-cards/create')}>
              <Plus className="w-4 h-4 mr-2" />
              Özet Kartı Oluştur
            </Button>
          </Card>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {cards.map((card) => (
                <Card key={card.id} className="p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: card.color }}
                        />
                        <Badge variant={getDifficultyColor(card.difficulty)}>
                          {getDifficultyLabel(card.difficulty)}
                        </Badge>
                        <Badge variant={card.isPublished ? 'success' : 'secondary'}>
                          {card.isPublished ? 'Yayında' : 'Taslak'}
                        </Badge>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1 line-clamp-2">
                        {card.title}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {getSubjectName(card.subject)} • {card.topic}
                      </p>
                    </div>
                    
                    <div className="relative">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          // Toggle dropdown menu
                        }}
                      >
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="mb-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3">
                      {card.content.substring(0, 150)}...
                    </p>
                  </div>

                  <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mb-4">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-1">
                        <Eye className="w-4 h-4" />
                        <span>{card.viewCount}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Heart className="w-4 h-4" />
                        <span>{card.likeCount}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Clock className="w-4 h-4" />
                        <span>{card.estimatedReadTime} dk</span>
                      </div>
                    </div>
                  </div>

                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                    {formatDistanceToNow(new Date(card.createdAt), { 
                      addSuffix: true, 
                      locale: tr 
                    })}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Button
                        onClick={() => navigate(`/admin/summary-cards/edit/${card.id}`)}
                        variant="outline"
                        size="sm"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => {
                          setSelectedCard(card)
                          setShowDeleteModal(true)
                        }}
                        variant="outline"
                        size="sm"
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <Button
                      onClick={() => handleTogglePublish(card)}
                      variant={card.isPublished ? 'outline' : 'default'}
                      size="sm"
                    >
                      {card.isPublished ? 'Yayından Kaldır' : 'Yayınla'}
                    </Button>
                  </div>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between mt-8">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {pagination.total} sonuçtan {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, pagination.total)} arası gösteriliyor
                </div>
                
                <div className="flex items-center space-x-2">
                  <Button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    variant="outline"
                    size="sm"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      const page = i + 1
                      return (
                        <Button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          variant={currentPage === page ? 'default' : 'outline'}
                          size="sm"
                        >
                          {page}
                        </Button>
                      )
                    })}
                  </div>
                  
                  <Button
                    onClick={() => setCurrentPage(prev => Math.min(pagination.totalPages, prev + 1))}
                    disabled={currentPage === pagination.totalPages}
                    variant="outline"
                    size="sm"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false)
          setSelectedCard(null)
        }}
        title="Özet Kartını Sil"
      >
        <div className="p-6">
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            "{selectedCard?.title}" başlıklı özet kartını silmek istediğinizden emin misiniz? 
            Bu işlem geri alınamaz.
          </p>
          
          <div className="flex items-center justify-end space-x-3">
            <Button
              onClick={() => {
                setShowDeleteModal(false)
                setSelectedCard(null)
              }}
              variant="outline"
            >
              İptal
            </Button>
            <Button
              onClick={handleDelete}
              variant="destructive"
            >
              Sil
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default AdminSummaryCards